import React, { useContext, useEffect } from 'react';
import { apiUserAccountService } from '@api/services/account.apiService';
import { ButtonIcon } from '@components-library/ButtonIcon/ButtonIcon';
import { Dropdown, IDropDownProps } from '@components-library/Dropdown/Dropdown';
import { CountryFlagIcon } from '@components-library/Icons/iconsComponents';
import { GENERAL_CONSTANTS } from '@components-shared/general.constants';
import { pushLanguageSelectorEvent } from '@components-shared/googleAnalyticsEvents';
import { isLocaleExists } from '@locales/helpers';
import { keyMap } from '@locales/lokalise/keyMap';
import { handleError } from '@utils/errors';
import { cookieService } from '@utils/library/cookie.service';
import cn from 'classnames';
import { AppContext } from 'contextStore/context';
import { ENUM_LOCALE_CODE } from 'global.enums';
import { LanguageType } from 'global.interfaces';
import { useMobxStore } from 'store/mobx';
import styles from './DropdownLanguageSwitch.styles.module.scss';

interface IDropdownLanguageSwitch {
  className?: string;
}

export const DropdownLanguageSwitch = ({ className }: IDropdownLanguageSwitch) => {
  const { language, i18next, setLanguage } = useContext(AppContext);
  const {
    accountStore: { initialUserInfo },
  } = useMobxStore();

  const locales = GENERAL_CONSTANTS.LOCALES;
  const localeKeys = Object.keys(locales);

  const items: IDropDownProps['options'] = localeKeys.map((localeKey) => ({
    label: locales[localeKey as LanguageType].title,
    key: locales[localeKey as LanguageType].key,
    icon: <CountryFlagIcon locale={locales[localeKey as LanguageType].key} className={styles.iconColor} />,
  }));

  const setLocale = async (language: string) => {
    try {
      const {
        data: { culture },
      } = await apiUserAccountService.setLanguage(language);

      if (initialUserInfo) {
        await apiUserAccountService.updateLanguage(language);
      }

      setLanguage(culture);

      pushLanguageSelectorEvent(language);

      document.body.className = language;
    } catch (error) {
      return handleError(keyMap.errorsUserCantChangeLanguage, error);
    }
  };

  useEffect(() => {
    if (isLocaleExists(language) && language !== cookieService.getCookie('__culture')) {
      void setLocale(language);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (language !== i18next.language) {
      setLanguage(i18next.language as ENUM_LOCALE_CODE);
    }
  }, [i18next.language, language, setLanguage]);

  return (
    <Dropdown
      className={cn(styles.iconColor, className)}
      classNameRoot={cn(styles.root, styles.dropdownInnerStyles)}
      options={items}
      onSelect={({ key }) => setLocale(key)}
      selectable={true}
      selectedKeys={[language]}
      triggerComponent={
        <ButtonIcon className={styles.button}>
          <CountryFlagIcon locale={language} size={32} />
        </ButtonIcon>
      }
    />
  );
};
